

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  TextField,
  Tooltip,
  Pagination,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import AllStatusTable from "./common/AllStatusTable";
import { ArrowBack } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import paymentportalservice from "../../services/paymentportalservice";
import PaymentFilter from "./PaymentFilter";
import { useDebounce } from "../../hooks/useDebounce";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import GetCookieByName from "../../hooks/GetCookie";
import jwtDecode from "jwt-decode";
import useAccess from "../../hooks/useAccess";

const PAGE_SIZE = 100;

const VALID_STATUS_FILTERS = ["all", "pending", "applied", "waiting", "underprocess", "success", "failed", "rejected"];

export default function PaymentStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  // Lets a caller (e.g. a status tile on the campaign details page) deep-link
  // straight into one status instead of always landing on "All".
  const [filterStatus, setFilterStatus] = useState(
    VALID_STATUS_FILTERS.includes(location.state?.status) ? location.state.status : "all"
  );
  const [filterPoc, setFilterPoc] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  // Oldest-due-date-first by default (i.e. most overdue first) once Overdue
  // is checked — only meaningful (and only sent to the backend) while it is.
  const [overdueSort, setOverdueSort] = useState("asc");
  const [pocs, setPocs] = useState([]);

  const debouncedSearch = useDebounce(searchTerm, 500);
  // Tracks the most recently *issued* fetchStatus call so a slower, stale
  // response (e.g. from before the user flipped a filter) can't overwrite
  // a faster response that landed first — filterPoc/overdueOnly/overdueSort
  // are all now instantly re-triggerable (no debounce), so requests can
  // resolve out of order.
  const latestRequestId = useRef(0);

  // React Router doesn't remount this component for a same-path navigation,
  // so seeding filterStatus once via useState's initializer only takes
  // effect on the very first visit — clicking a second status tile (e.g.
  // "Failed" after "Pending") from elsewhere would otherwise leave the table
  // stuck on whichever status was selected first. Re-sync whenever a fresh
  // navigation actually changes location.state.
  useEffect(() => {
    if (VALID_STATUS_FILTERS.includes(location.state?.status)) {
      setFilterStatus(location.state.status);
      // A deep-link from elsewhere (e.g. a status tile) implies "show me
      // exactly this status" — any POC/Overdue filter left over from
      // whatever the user had set while previously browsing this page
      // would otherwise silently narrow that view without any indication.
      setFilterPoc("");
      setOverdueOnly(false);
      setOverdueSort("asc");
      // Matches handleFilterChange's own reset — without it, switching
      // status while parked past page 1 could request a page that doesn't
      // exist under the new filter and render an empty, out-of-range table.
      setCurrentPage(1);
    }
  }, [location.state]);

  const token = JSON.parse(GetCookieByName("auth"))["token"];
  const decoded = jwtDecode(token);
  // Gates the Waiting -> Under Process action further down. Reuses the
  // existing useAccess hook's isSuperUser (computed the same way, off the
  // same decoded claim) instead of re-deriving it here — it's independent
  // of isAdmin, so this can't affect the existing isAdmin-gated Approve
  // action below, which still reads decoded.isAdmin directly as before.
  const { isSuperUser } = useAccess();

  // Fetched once — the POC dropdown's options don't depend on any of the
  // other filters below, just on who's allowed to see what (enforced
  // server-side, same as the main list fetch).
  useEffect(() => {
    paymentportalservice
      .FetchPaymentStatusPOCs()
      .then(setPocs)
      .catch((error) => console.error("Error fetching POC filter options:", error));
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [currentPage, debouncedSearch, filterStatus, filterPoc, overdueOnly, overdueSort]);

  async function fetchStatus() {
    const requestId = ++latestRequestId.current;

    try {
      setLoading(true);

      console.time("fetchStatus");
      const response = await paymentportalservice.FetchPaymentStatus({
        page: currentPage,
        search: debouncedSearch,
        status: filterStatus,
        limit: PAGE_SIZE,
        poc: filterPoc,
        overdue: overdueOnly,
        overdueSort,
      });
      console.timeEnd("fetchStatus");

      // A newer request has since been issued (e.g. the user toggled
      // another filter before this one resolved) — let its response win
      // instead of clobbering the table with this stale one.
      if (requestId !== latestRequestId.current) {
        return;
      }

      const { data, totalItems, totalPages } = response;

      setPaymentData(data);
      setTotalPages(totalPages || 1);
      setTotalItems(totalItems || 0);
    }
    catch (error) {
      if (requestId !== latestRequestId.current) {
        return;
      }

      console.error("Error fetching payment status:", error);
      setPaymentData([]);
      setTotalItems(0);
    }
    finally {
      if (requestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  // Shared by every filter control below: apply the new value, then reset
  // to page 1 since the previous page number may not exist under the new
  // filter.
  const applyFilter = (setter) => (newValue) => {
    setter(newValue);
    setCurrentPage(1);
  };

  const handleFilterChange = applyFilter(setFilterStatus);
  const handlePocChange = applyFilter(setFilterPoc);
  const handleOverdueSortChange = applyFilter(setOverdueSort);

  const handleOverdueChange = (newOverdueOnly) => {
    setOverdueOnly(newOverdueOnly);
    // overdueSort is only meaningful (and only sent to the backend) while
    // Overdue is checked — reset it here so re-checking Overdue always
    // starts from the documented oldest-first default instead of whatever
    // direction was left over from a previous session.
    setOverdueSort("asc");
    setCurrentPage(1);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCopy = (row) => {
    let targeturl = process.env.REACT_APP_FORM_BASE_URL;

    if (row.isCompany) {
      targeturl += "/company"
    }
    else {
      targeturl += "/influencer"
    }

    targeturl += `/payment/${row.pathName}`;

    navigator.clipboard.writeText(targeturl);

    toast.success(
      'Link Copied!',
      {
        position: 'top-right'
      }
    );
  }

  const handleDecline = async (id, payableAmount) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    await paymentportalservice.DeletePaymentLink({
      id,
      payableAmount,
    });

    await Swal.fire({
      title: "Deleted!",
      text: "Your payment link has been deleted.",
      icon: "success",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const handleApprove = async (id) => {
    try {
      const user = JSON.parse(GetCookieByName("user"));

      let postdata = {
        rowId: id,
        id: user._id
      };

      const response = await paymentportalservice.ApprovePayment(postdata);
      fetchStatus();
    }
    catch (error) {

    }
  }

  // Shared by handleMarkUnderProcess/handleRevertToWaiting below — both take
  // one id (a per-row action) or an array (the bulk toolbar). Only links
  // actually eligible server-side (still Waiting, and for the revert case
  // already marked Under Process — see routes/paymentLink.js's
  // updateWaitingPaymentLinks) get touched, so a mixed bulk selection is
  // never an error; the closing message reflects how many actually
  // changed vs. how many were requested.
  const runWaitingTransition = async (ids, { confirmTitle, confirmText, confirmButtonText, serviceFn, successVerb }) => {
    const idList = Array.isArray(ids) ? ids : [ids];
    if (!idList.length) return;

    const result = await Swal.fire({
      title: idList.length > 1 ? `${confirmTitle} (${idList.length} selected)` : confirmTitle,
      text: confirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await serviceFn(idList);
      const modified = response?.modified ?? 0;

      await Swal.fire({
        icon: modified > 0 ? "success" : "info",
        title: modified > 0 ? "Updated!" : "Nothing to update",
        text: modified > 0
          ? `${successVerb} ${modified} link(s).`
          : "None of the selected links were eligible.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchStatus();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not update",
        text: error?.response?.data || error.message,
      });
    }
  };

  // Super-admin-only (every icon/button that calls these is itself hidden
  // from everyone else — see PaymentActions.jsx and AllStatusTable.jsx).
  // The backend re-checks isSuperUser independently either way, so this is
  // UI convenience, not the actual access boundary.
  const handleMarkUnderProcess = (ids) =>
    runWaitingTransition(ids, {
      confirmTitle: "Mark as Under Process?",
      confirmText: "This moves Waiting link(s) into Under Process.",
      confirmButtonText: "Yes, mark it",
      serviceFn: paymentportalservice.MarkPaymentsUnderProcess,
      successVerb: "Marked",
    });

  const handleRevertToWaiting = (ids) =>
    runWaitingTransition(ids, {
      confirmTitle: "Revert to Waiting?",
      confirmText: "This undoes Under Process, back to a plain Waiting.",
      confirmButtonText: "Yes, revert it",
      serviceFn: paymentportalservice.RevertPaymentsToWaiting,
      successVerb: "Reverted",
    });

  const start = (currentPage - 1) * PAGE_SIZE + 1;
  const end = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <>
      <Box
        width={{ xs: "100%", lg: `calc(100% - 80px)` }}
        marginLeft={{ xs: "0px", lg: `80px` }}
        marginTop={8}
        // py={2}
        backgroundColor={"#F0F2F5"}
        minHeight={"100vh"}
        height={"100%"}
      >
        {/* <PaymentFilter
          filterStatus={filterStatus}
          onFilterChange={handleFilterChange}
          filteredCount={paymentData?.length}
          totalCount={totalItems}
        /> */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 2,
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0"
          }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            columnGap={1}
          >
            <Tooltip
              title="Go Back"
            >
              <ArrowBack
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(-1)}
                fontSize="small"
              />
            </Tooltip>

            <Chip
              label={`${totalItems}`}
              size="small"
              variant="filled"
              sx={{
                backgroundColor: "#FFC107",
                color: "#000000",
                alignSelf: "center",
                fontSize: 11
              }}
            />

            <Typography
              fontSize={11}
              fontFamily={'Inter'}
            >
              Showing {start} to {end} of {totalItems} Links
            </Typography>
          </Stack>

          <TextField
            id="outlined-search"
            placeholder="Search Campaign || Influencer || Pancard || Amount"
            type="search"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              minWidth: 400
            }}
            inputProps={{
              style: {
                fontSize: 12
              }
            }}
          />

          <PaymentFilter
            filterStatus={filterStatus}
            onFilterChange={handleFilterChange}
            filteredCount={paymentData?.length}
            totalCount={totalItems}
            pocs={pocs}
            filterPoc={filterPoc}
            onPocChange={handlePocChange}
            overdueOnly={overdueOnly}
            onOverdueChange={handleOverdueChange}
            overdueSort={overdueSort}
            onOverdueSortChange={handleOverdueSortChange}
          />
        </Box>

        <Box marginTop={2}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <AllStatusTable
                data={paymentData}
                loading={loading}
                onLinkCopy={handleCopy}
                onDelete={handleDecline}
                onApprove={handleApprove}
                isAdmin={decoded.isAdmin}
                overdueOnly={overdueOnly}
                isSuperUser={isSuperUser}
                onMarkUnderProcess={handleMarkUnderProcess}
                onRevertToWaiting={handleRevertToWaiting}
              />

              {totalPages > 1 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  mt={2}
                  // mb={2}
                  sx={{
                    backgroundColor: "#FFFFFF",
                    py: 2
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
}